/**
 * Contact form validation + draft handling.
 *
 * - Accidental refresh while typing: restore draft text
 * - After successful submit / revisit: keep fields empty
 */
(function () {
  const EMAIL_PATTERN =
    /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
  const NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z][A-Za-z\s.'’-]*$/;
  const PHONE_MIN_DIGITS = 7;
  const PHONE_MAX_DIGITS = 15;
  const DRAFT_PREFIX = 'tolvv-contact-draft:';
  const SENT_PREFIX = 'tolvv-contact-sent:';

  const FIELD_ORDER = [
    { key: 'name', blank: 'name_blank', invalid: 'name_invalid' },
    { key: 'email', blank: 'email_blank', invalid: 'email_invalid' },
    { key: 'phone', blank: 'phone_blank', invalid: 'phone_invalid' },
    { key: 'subject', blank: 'subject_blank', minLen: 8, minMsg: 'subject_min' },
    { key: 'message', blank: 'comment_blank', minLen: 20, minMsg: 'comment_min' },
  ];

  function draftKey(formId) {
    return `${DRAFT_PREFIX}${formId || 'default'}`;
  }

  function sentKey(formId) {
    return `${SENT_PREFIX}${formId || 'default'}`;
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Ignore storage failures.
    }
  }

  function storageRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore storage failures.
    }
  }

  function readDraft(formId) {
    try {
      const raw = storageGet(draftKey(formId));
      if (!raw) return null;
      const data = JSON.parse(raw);
      return data && typeof data === 'object' ? data : null;
    } catch {
      return null;
    }
  }

  function writeDraft(formId, data) {
    storageSet(draftKey(formId), JSON.stringify(data));
  }

  function clearDraft(formId) {
    storageRemove(draftKey(formId));
  }

  function markSent(formId) {
    storageSet(sentKey(formId), '1');
  }

  function clearSent(formId) {
    storageRemove(sentKey(formId));
  }

  function wasSent(formId) {
    return storageGet(sentKey(formId)) === '1';
  }

  function collectDraft(form) {
    const data = {};
    FIELD_ORDER.forEach(({ key }) => {
      const input = form.querySelector(`[data-validate="${key}"]`);
      data[key] = input ? input.value || '' : '';
    });
    return data;
  }

  function restoreDraft(form, formId) {
    const draft = readDraft(formId);
    if (!draft) return;

    FIELD_ORDER.forEach(({ key }) => {
      const input = form.querySelector(`[data-validate="${key}"]`);
      if (!input) return;
      if (typeof draft[key] === 'string' && draft[key] !== '') {
        input.value = draft[key];
      }
    });
  }

  function clearFormFields(form) {
    form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]), textarea').forEach((field) => {
      field.value = '';
      field.classList.remove('is-invalid');
      field.removeAttribute('aria-invalid');
    });
    form.querySelectorAll('.contact-form__field-error').forEach((errorEl) => {
      errorEl.textContent = '';
      errorEl.hidden = true;
    });
  }

  function decodeEntities(value) {
    if (!value || !value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function parseMessages(formId) {
    const el = document.getElementById(`${formId}-messages`);
    if (!el) return {};
    try {
      const raw = JSON.parse(el.textContent || '{}');
      return Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, decodeEntities(String(value))])
      );
    } catch {
      return {};
    }
  }

  function isValidName(value) {
    if (/\d/.test(value)) return false;
    return NAME_PATTERN.test(value);
  }

  function isValidEmail(value) {
    if (!EMAIL_PATTERN.test(value)) return false;
    const domain = value.split('@')[1] || '';
    return /\.[A-Za-z]{2,}$/.test(domain);
  }

  function isValidPhone(value) {
    if (!/^[0-9+\-\s().]*$/.test(value)) return false;
    const digits = value.replace(/\D/g, '');
    return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
  }

  function validateField(field, value, messages) {
    const trimmed = value.trim();

    if (!trimmed) {
      return messages[field.blank] || 'This field is required';
    }

    if (field.minLen && trimmed.length < field.minLen) {
      return messages[field.minMsg] || `Must be at least ${field.minLen} characters (currently: ${trimmed.length})`;
    }

    if (field.key === 'name' && !isValidName(trimmed)) {
      return messages[field.invalid] || 'Enter a valid name using letters only';
    }

    if (field.key === 'email' && !isValidEmail(trimmed)) {
      return messages[field.invalid] || 'Enter a valid email (e.g. name@example.com)';
    }

    if (field.key === 'phone' && !isValidPhone(trimmed)) {
      return messages[field.invalid] || 'Enter a valid phone number (7-15 digits)';
    }

    return null;
  }

  function collectErrors(form, messages) {
    const errors = [];

    FIELD_ORDER.forEach((field) => {
      const input = form.querySelector(`[data-validate="${field.key}"]`);
      if (!input) return;
      const message = validateField(field, input.value || '', messages);
      if (message) errors.push({ message, input, key: field.key });
    });

    return errors;
  }

  function clearFieldError(form, input) {
    input.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
    const errorEl = form.querySelector(`[data-error-for="${input.dataset.validate}"]`);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function showFieldError(form, input, message) {
    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    const errorEl = form.querySelector(`[data-error-for="${input.dataset.validate}"]`);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function initContactForm(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.validationBound === 'true') return;
    form.dataset.validationBound = 'true';
    form.setAttribute('novalidate', 'novalidate');
    form.setAttribute('autocomplete', 'off');

    const formId = form.id;
    const messages = parseMessages(formId);
    const inputs = FIELD_ORDER.map(({ key }) => form.querySelector(`[data-validate="${key}"]`)).filter(Boolean);

    const successBanner =
      form.querySelector('[data-contact-success]') ||
      form.closest('.contact-form')?.querySelector('[data-contact-success]');

    if (successBanner || wasSent(formId)) {
      // Successful submit or revisit after submit → keep form empty.
      markSent(formId);
      clearDraft(formId);
      clearFormFields(form);
    } else {
      // Accidental refresh while typing → restore draft.
      restoreDraft(form, formId);
    }

    const saveDraft = () => writeDraft(formId, collectDraft(form));

    const runValidation = () => {
      inputs.forEach((input) => clearFieldError(form, input));
      const errors = collectErrors(form, messages);

      errors.forEach(({ input, message }) => showFieldError(form, input, message));

      if (errors.length > 0) {
        errors[0].input?.focus({ preventScroll: false });
        errors[0].input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      return true;
    };

    form.addEventListener('submit', (event) => {
      if (!runValidation()) {
        event.preventDefault();
        saveDraft();
        return;
      }

      // Mark as sent before redirect so revisit/refresh stays empty.
      markSent(formId);
      clearDraft(formId);
    });

    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        // User started a new message → allow drafts again.
        clearSent(formId);
        clearFieldError(form, input);
        saveDraft();
      });
      input.addEventListener('blur', () => {
        const field = FIELD_ORDER.find((item) => item.key === input.dataset.validate);
        if (!field) return;
        const message = validateField(field, input.value || '', messages);
        clearFieldError(form, input);
        if (message && (input.value || '').trim() !== '') {
          showFieldError(form, input, message);
        }
        if (!wasSent(formId)) saveDraft();
      });
    });
  }

  function initAll() {
    document.querySelectorAll('form.contact-form__form').forEach(initContactForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
